import React from 'react';

import WidgetInput from './WidgetInput';
import { format } from 'date-fns';
import uriTemplates from 'uri-templates';
import DatePicker, { DatePickerProps, registerLocale } from 'react-datepicker';

import { da } from 'date-fns/locale/da';
import { de } from 'date-fns/locale/de';
import { enGB } from 'date-fns/locale/en-GB'; // Default is en-US
import { fr } from 'date-fns/locale/fr';
import { nl } from 'date-fns/locale/nl';
import { sv } from 'date-fns/locale/sv';
import PropsDate from './interfaces/PropsDateInterface';

registerLocale('da', da);
registerLocale('de', de);
registerLocale('en', enGB);
registerLocale('fr', fr);
registerLocale('nl', nl);
registerLocale('sv', sv);

const validLocales = ['da', 'de', 'en', 'fr', 'nl', 'sv'];

export interface WidgetDateState {
    allowedDates: string[];
    fullDates: string[];
}

export default class WidgetDate extends WidgetInput<PropsDate, WidgetDateState> {
    private initialized: boolean = false;
    private allowedDateshref?: string;
    private monthsLoaded: string[];
    private wrapperRef = React.createRef<HTMLDivElement>();

    constructor(props: PropsDate) {
        super(props);

        this.allowedDateshref =
            props._links && props._links['recras:bookprocess:options']
                ? props._links['recras:bookprocess:options'].href
                : null;

        this.monthsLoaded = [];
        this.state = { allowedDates: [], fullDates: [] };
    }

    onChange(date: any) {
        this.props.handleChange(this.props.name, format(date, 'yyyy-MM-dd'));
    }

    getRenderValueAsDate() {
        let val = this.getRenderValue();
        if (val) {
            // new Date('2022-10-24') is being interpreted by the browser as being in the local timezone.
            // When the local date is different from the UTC date, this causes the datepicker to show it
            // on the wrong date.
            return new Date(
                new Date(val).toLocaleString('en-US', {
                    timeZone: 'UTC',
                })
            );
        }
        return null;
    }

    fetchMonths(baseDate: Date, numberOfMonths: number) {
        for (let i = 0; i < numberOfMonths; i++) {
            let date = new Date(baseDate.getFullYear(), baseDate.getMonth() + i, 1);
            this.fetchMonth(date);
        }
    }

    fetchMonth(date: Date) {
        let monthString = format(date, 'yyyy-MM');

        if (this.monthsLoaded.includes(monthString)) {
            return;
        }

        let data = this.props.dataAsUrlSearchParams();

        let template = uriTemplates(this.allowedDateshref);

        let expandedUri = template.fill({
            firstdate: format(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
            lastdate: format(new Date(date.getFullYear(), date.getMonth() + 1, 0), 'yyyy-MM-dd'),
        });

        // push value before the promise, so we don't fetch the same months twice when
        // this function gets called twice before the first promise resolves
        this.monthsLoaded.push(monthString);
        return fetch(expandedUri, { method: 'POST', body: data }) // TODO: get method from Link
            .then(resp => resp.json())
            .then(result => {
                this.setState(state => {
                    return {
                        allowedDates: state.allowedDates.concat(result.options.allowedDates),
                        fullDates: state.fullDates.concat(result.options.fullDates),
                    };
                });
            });
    }

    isDateAllowed(date: Date) {
        return (
            this.state.allowedDates.includes(format(date, 'yyyy-MM-dd')) &&
            !this.state.fullDates.includes(format(date, 'yyyy-MM-dd'))
        );
    }

    getDatesFullyBooked() {
        return this.state.fullDates.map((date: string) => {
            return new Date(date);
        });
    }

    render() {
        let options: DatePickerProps;

        options = {
            onChange: this.onChange,
            selected: this.getRenderValueAsDate(),
            dateFormat: 'dd-MM-yyyy',
            id: this.props.id,
            name: this.props.name,
            popperPlacement: 'bottom-start',
            required: this.props.required,
            disabled: this.props.disabled,
        };

        if (this.props.settings.locale && validLocales.includes(this.props.settings.locale)) {
            options.locale = this.props.settings.locale;
        }

        if (this.props.settings.minDate) {
            options.minDate = new Date(this.props.settings.minDate);
        }

        if (this.props.value && !this.initialized) {
            options.value = this.props.value;
            this.initialized = true; //HACK to set the preselected date on initialisation, but allow free editing afterwards
        }

        const monthsShown = 1;
        if (this.allowedDateshref) {
            options.filterDate = this.isDateAllowed.bind(this);
            options.highlightDates = [
                { 'react-datepicker__day--fully-booked': this.getDatesFullyBooked() },
            ];
            options.onMonthChange = (date: Date) => this.fetchMonths(date, monthsShown);
            options.onSelect = (date: Date) => this.fetchMonths(date, monthsShown);
            const initialDate = this.props.value ? new Date(this.props.value) : new Date();
            this.fetchMonths(initialDate, monthsShown);
        }

        if (this.props.settings.presentation === 'calendar') {
            options.inline = true;
            options.shouldCloseOnSelect = false;
            options.focusSelectedMonth = true;
            options.monthsShown = monthsShown;
            options.disabledKeyboardNavigation = true;
        }

        return React.createElement(
            'div',
            {
                ref: this.wrapperRef,
                className: this.props.className,
            },
            React.createElement(DatePicker, options)
        );
    }
}
